import {
  FC,
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type WheelEventHandler,
} from 'react';
import { Loader } from 'lucide-react';

import { NormalizedFile } from '@/types/file-explorer-types';

import IconChevronLeft from '@/assets/svg/icon-chevron-left.svg?react';
import IconChevronRight from '@/assets/svg/icon-chevron-right.svg?react';
import IconClose from '@/assets/svg/icon-close-out.svg?react';
import IconDocx from '@/assets/svg/icon-docx.svg?react';
import IconMaximize from '@/assets/svg/icon-expand.svg?react';
import IconFile from '@/assets/svg/icon-file.svg?react';
import IconCompare from '@/assets/svg/icon-img-copy.svg?react';
import IconDownload from '@/assets/svg/icon-img-download.svg?react';
import IconNewPage from '@/assets/svg/icon-img-new-page.svg?react';
import IconImageRotateDown from '@/assets/svg/icon-img-rotate-down.svg?react';
import IconImageRotateUp from '@/assets/svg/icon-img-rotate-up.svg?react';
import IconZoomIn from '@/assets/svg/icon-img-zoom-in.svg?react';
import IconZoomOut from '@/assets/svg/icon-img-zoom-out.svg?react';
import IconImg from '@/assets/svg/icon-img.svg?react';
import IconTxt from '@/assets/svg/icon-txt.svg?react';

import { cn, getFileExtensionFromString } from '@/lib/utils';

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';

import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';

interface ImageTransform {
  scale: number;
  rotation: number;
}

interface PhotoViewerProps {
  items: NormalizedFile[];
  currentIndex: number;
  onNavigate: (direction: 'prev' | 'next') => void;
  isPhotoViewerOpen: boolean;
  setIsPhotoViewerOpen: (open: boolean) => void;
  setIsCompareDialogOpen: (open: boolean) => void;
  isStandalone?: boolean;
}

export const PhotoViewer: FC<PhotoViewerProps> = ({
  items,
  currentIndex,
  onNavigate,
  isPhotoViewerOpen,
  setIsPhotoViewerOpen,
  setIsCompareDialogOpen,
  isStandalone,
}) => {
  const [transform, setTransform] = useState<ImageTransform>({
    scale: 1,
    rotation: 0,
  });
  const [isMaximized, setIsMaximized] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [position, setPosition] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });
  const isDraggingRef = useRef(false);
  const dragStartPointRef = useRef<{ x: number; y: number } | null>(null);
  const startPositionRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const handleMaximize = () => {
    setIsMaximized((prev) => !prev);
  };

  const handleNavigate = (direction: 'prev' | 'next') => {
    setTransform({ scale: 1, rotation: 0 });
    setPosition({ x: 0, y: 0 });
    onNavigate(direction);
  };

  const handleZoom = (direction: 'in' | 'out') => {
    setTransform((prev) => ({
      ...prev,
      scale:
        direction === 'in'
          ? Math.min(prev.scale + 0.25, 3)
          : Math.max(prev.scale - 0.25, 0.5),
    }));
  };

  const handleRotate = (direction: 'up' | 'down') => {
    setTransform((prev) => ({
      ...prev,
      rotation: direction === 'up' ? prev.rotation + 90 : prev.rotation - 90,
    }));
  };

  const handleWheel: WheelEventHandler<HTMLDivElement> = (e) => {
    // Use wheel to zoom in/out over the preview area
    e.preventDefault();
    const isScrollUp = e.deltaY < 0;
    setTransform((prev) => ({
      ...prev,
      scale: isScrollUp
        ? Math.min(prev.scale + 0.25, 3)
        : Math.max(prev.scale - 0.25, 0.5),
    }));
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(currentItem?.file_link!);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = currentItem?.name || 'image';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {}
  };

  const currentItem: NormalizedFile | undefined = items[currentIndex];

  const handleCompareImage = () => {
    setIsPhotoViewerOpen(false);
    setIsCompareDialogOpen(true);
  };

  const renderPreview = (item?: NormalizedFile) => {
    if (!item) return null;
    const extension = getFileExtensionFromString(item.name);
    switch (extension) {
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
      case 'tiff':
        return (
          <div
            className="relative flex h-full w-full items-center justify-center"
            onWheel={handleWheel}
            onMouseDown={(e: ReactMouseEvent<HTMLDivElement>) => {
              if (!currentItem?.file_type?.startsWith('image/')) return;
              e.preventDefault();
              isDraggingRef.current = true;
              dragStartPointRef.current = { x: e.clientX, y: e.clientY };
              startPositionRef.current = { ...position };
            }}
            onMouseMove={(e: ReactMouseEvent<HTMLDivElement>) => {
              if (!isDraggingRef.current || !dragStartPointRef.current) return;
              e.preventDefault();
              const deltaX = e.clientX - dragStartPointRef.current.x;
              const deltaY = e.clientY - dragStartPointRef.current.y;
              setPosition({
                x: startPositionRef.current.x + deltaX,
                y: startPositionRef.current.y + deltaY,
              });
            }}
            onMouseUp={() => {
              if (!isDraggingRef.current) return;
              isDraggingRef.current = false;
              dragStartPointRef.current = null;
            }}
            onMouseLeave={() => {
              if (!isDraggingRef.current) return;
              isDraggingRef.current = false;
              dragStartPointRef.current = null;
            }}
          >
            {isImageLoading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader className="h-10 w-10 animate-spin-slow stroke-white" />
              </div>
            )}
            <div
              className={cn(
                isDraggingRef.current ? 'cursor-grabbing' : 'cursor-grab',
                'inline-block select-none',
              )}
              style={{
                transform: `translate(${position.x}px, ${position.y}px)`,
              }}
            >
              <img
                src={item.webp_url || item.file_link || ''}
                alt={item.name}
                className={cn(
                  'h-full max-h-[90vh] w-full max-w-full select-none overflow-hidden rounded-md object-contain transition-transform duration-200',
                  isImageLoading && 'opacity-0',
                )}
                style={{
                  transform: `scale(${transform.scale}) rotate(${transform.rotation}deg)`,
                }}
                onLoad={() => setIsImageLoading(false)}
                onError={(e) => {
                  // Fallback to original file_link if webp fails
                  const target = e.currentTarget as HTMLImageElement;
                  if (item.webp_url && target.src !== (item.file_link || '')) {
                    target.src = item.file_link || '';
                  }
                }}
                draggable={false}
              />
            </div>
          </div>
        );

      case 'heic':
      case 'heif':
        return (
          <div className="flex h-full w-full flex-col items-center justify-center rounded-md bg-gray-200">
            <IconImg className="h-[200px] w-[200px] fill-dark-red" />
            <p className="text-lg text-primary">{item.name}</p>
          </div>
        );

      case 'mov':
      case 'mp4':
        return (
          <div className="flex h-full w-full flex-col items-center justify-center rounded-md bg-gray-200">
            <video
              controls
              className="relative z-0 h-full w-full object-contain"
            >
              <source
                src={item.file_link ?? ''}
                type={`video/${item.name.split('.').pop()}`}
              />
              Ваш браузер не підтримує відео.
            </video>
          </div>
        );
      case 'txt':
        return (
          <div className="flex h-full w-full flex-col items-center justify-center rounded-md bg-gray-200">
            <IconTxt className="h-[200px] w-[200px]" />
            <p className="text-lg text-primary">{item.name}</p>
          </div>
        );

      case 'doc':
      case 'docx':
        return (
          <div className="flex h-full w-full flex-col items-center justify-center rounded-md bg-gray-200">
            <IconDocx className="h-[200px] w-[200px]" />
            <p className="text-lg text-primary">{item.name}</p>
          </div>
        );
      case 'pdf':
        return (
          <div className="h-full w-full">
            <div className="relative right-0 top-0 flex w-full justify-end py-8"></div>
            <div className="flex h-full w-full flex-1 flex-col items-stretch justify-start gap-0 overflow-auto border-none p-0 [&_#dialog-close]:hidden">
              <iframe
                src={`${currentItem?.file_link}#toolbar=1&navpanes=1&scrollbar=1`}
                className="h-full min-h-[80vh] w-full"
                title="PDF Viewer"
              />
            </div>
          </div>
        );

      default:
        return (
          <div className="flex h-full w-full flex-col items-center justify-center rounded-md bg-gray-200">
            <IconFile className="h-[200px] w-[200px] fill-dark-red" />
            <p className="text-lg text-primary">{item.name}</p>
          </div>
        );
    }
  };

  // Reset loading state when changing images
  useEffect(() => {
    setIsImageLoading(true);
  }, [currentIndex]);

  useEffect(() => {
    return () => {
      setTransform({ scale: 1, rotation: 0 });
      setIsMaximized(false);
      setPosition({ x: 0, y: 0 });
    };
  }, []);

  useEffect(() => {
    if (isPhotoViewerOpen) {
      setTransform({ scale: 1, rotation: 0 });
      setIsMaximized(false);
      setPosition({ x: 0, y: 0 });
    }
  }, [isPhotoViewerOpen]);

  // Keyboard controls: ArrowLeft/ArrowRight to navigate, R/L to rotate
  useEffect(() => {
    if (!isPhotoViewerOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || target?.isContentEditable)
        return;

      if (e.key === 'ArrowRight') {
        if (currentIndex < items.length - 1) {
          e.preventDefault();
          handleNavigate('next');
        }
        return;
      }
      if (e.key === 'ArrowLeft') {
        if (currentIndex > 0) {
          e.preventDefault();
          handleNavigate('prev');
        }
        return;
      }

      const isImage = currentItem?.file_type?.startsWith('image/');
      if (!isImage) return;

      if (e.key === 'r' || e.key === 'R') {
        e.preventDefault();
        handleRotate('up');
        return;
      }
      if (e.key === 'l' || e.key === 'L') {
        e.preventDefault();
        handleRotate('down');
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isPhotoViewerOpen, currentIndex, items.length, currentItem?.file_type]);

  const openInNewTab = (items: NormalizedFile[], currentIndex: number) => {
    const win = window.open('/viewer.html', '_blank');
    if (!win) return;

    const onReady = (e: MessageEvent) => {
      if (e.data?.type === 'viewer_ready') {
        win.postMessage(
          {
            type: 'init_viewer',
            payload: { items, currentIndex },
          },
          '*',
        );
        window.removeEventListener('message', onReady);
      }
    };

    window.addEventListener('message', onReady);
  };

  return (
    <Dialog open={isPhotoViewerOpen} onOpenChange={setIsPhotoViewerOpen}>
      <DialogContent
        className={cn(
          'flex items-center justify-center rounded-md border-none bg-transparent p-0 [&_#dialog-close]:hidden',
          isMaximized && !isStandalone
            ? 'h-screen w-screen max-w-none'
            : 'max-h-[90vh] max-w-[900px]',
        )}
      >
        <DialogTitle className="mb-3 text-2xl font-semibold" hidden>
          Фотографія
        </DialogTitle>
        <DialogDescription hidden>{currentItem?.name}</DialogDescription>
        <div
          className={cn(
            'relative flex items-center justify-center',
            isMaximized && !isStandalone
              ? 'h-screen w-screen'
              : 'sm:h-[700px] sm:w-[900px]',
          )}
        >
          {!isStandalone && (
            <>
              <DialogClose className="absolute right-5 top-5 z-50 transition-colors hover:[&_svg]:fill-primary">
                <IconClose className="h-[20px] w-[20px] fill-white transition-colors" />
              </DialogClose>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    className="absolute right-14 top-5 z-50 transition-colors hover:[&_svg]:fill-primary"
                    onClick={handleMaximize}
                  >
                    <IconMaximize className="h-[20px] w-[20px] fill-white transition-colors" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Збільшити</p>
                </TooltipContent>
              </Tooltip>
            </>
          )}
          {/* Navigation buttons */}
          <>
            <button
              className={cn(
                'absolute -left-14 z-50 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-[#002C4180] text-white hover:bg-[#002C4180]/20',
                currentIndex === 0 && 'hidden',
              )}
              onClick={() => handleNavigate('prev')}
            >
              <IconChevronLeft className="h-4 w-[10px] fill-white" />
            </button>

            <button
              className={cn(
                'absolute -right-14 z-50 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-[#002C4180] text-white outline-none hover:bg-[#002C4180]/20',
                currentIndex === items.length - 1 && 'hidden',
              )}
              onClick={() => handleNavigate('next')}
            >
              <IconChevronRight className="h-4 w-[10px] fill-white" />
            </button>
          </>
          {/* Main image */}
          {renderPreview(currentItem)}

          {/* Controls toolbar */}
          {currentItem?.file_type.startsWith('image/') && (
            <div className="absolute bottom-[-60px] left-1/2 flex h-[50px] -translate-x-1/2 items-center justify-center gap-14 rounded-md bg-[#002C4180] p-2 backdrop-blur-sm sm:bottom-[28px] sm:w-[400px]">
              <div className="flex items-center">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      className="rounded-sm p-2 text-white hover:bg-white/20"
                      onClick={() => handleRotate('up')}
                    >
                      <IconImageRotateUp className="h-5 w-5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Повернути за годинниковою стрілкою</p>
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      className="rounded-sm p-2 text-white hover:bg-white/20"
                      onClick={() => handleRotate('down')}
                    >
                      <IconImageRotateDown className="h-5 w-5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Повернути проти годинникової стрілки</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="flex items-center">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      className="rounded-sm p-2 text-white hover:bg-white/20"
                      onClick={() => handleZoom('in')}
                    >
                      <IconZoomIn className="h-5 w-5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Збільшити</p>
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      className="rounded-sm p-2 text-white hover:bg-white/20"
                      onClick={() => handleZoom('out')}
                    >
                      <IconZoomOut className="h-5 w-5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Зменшити</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="flex items-center">
                {!isStandalone && (
                  <>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          className="rounded-sm p-2 text-white hover:bg-white/20"
                          onClick={() => openInNewTab(items, currentIndex)}
                        >
                          <IconNewPage className="h-5 w-5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Відкрити в новій вкладці</p>
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          className="rounded-sm p-2 text-white hover:bg-white/20"
                          onClick={handleCompareImage}
                        >
                          <IconCompare className="h-5 w-5" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Порівняти з іншим зображенням</p>
                      </TooltipContent>
                    </Tooltip>
                  </>
                )}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      className="rounded-sm p-2 text-white hover:bg-white/20"
                      onClick={handleDownload}
                    >
                      <IconDownload className="h-5 w-5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Скачати</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
